/**
 * soloProgressSync.ts — Voortgang van solo-accounts synchroniseren.
 *
 * WAAROM DIT BESTAAT
 * Een kind dat zichzelf registreert heeft wél een serveraccount, maar zijn
 * voortgang stond alleen op het apparaat waar hij die opbouwde. Inloggen op de
 * telefoon gaf een lege omgeving. Dat is geen privacywinst maar een kapot
 * product: het kind ziet zijn eigen werk niet terug.
 *
 * WAT WEL EN NIET MEEGAAT
 * De grens is niet "veel of weinig", maar OPERATIONEEL versus EVALUATIEF:
 *
 *   mee  — mastery, vraagstatussen, XP, streak, level.
 *          Werkgetallen die de adaptieve engine nodig heeft om NU een passende
 *          vraag te stellen. Zonder deze data is het product op een tweede
 *          apparaat simpelweg stuk.
 *
 *   niet — toetsuitslagen, rapporten, niveaubepalingen, en de NAAM.
 *          Dat zijn oordelen over een kind, of identificerende gegevens.
 *          Daarvoor is ouderlijke toestemming nodig; die blijven lokaal tot een
 *          ouder of school gekoppeld is.
 *
 * Die grens staat ook als constraint in de database (0009), dus een fout hier
 * wordt geweigerd in plaats van stilzwijgend geaccepteerd. Deze module is de
 * eerste verdedigingslinie, niet de enige.
 *
 * @see supabase/migrations/0009_solo_progress_sync.sql
 * @see supabase/migrations/0010_consent_wall.sql
 */

import { supabase } from './client'
import { error as logError, debug } from '../../utils/logger'

/**
 * De sleutels die mogen synchroniseren.
 *
 * Een ALLOWLIST, geen blocklist. Dat is het hele punt: voeg iemand later een
 * veld toe aan de leerlingstaat, dan gaat dat NIET automatisch mee. Bij een
 * blocklist zou een nieuw veld stilzwijgend naar de server lekken zodra iemand
 * vergeet het uit te sluiten — en juist bij kinddata is "per ongeluk meer
 * versturen" het scenario dat je structureel moet uitsluiten.
 */
const SYNCED_KEYS = [
  'progress',
  'questionStates',
  'familyMastery',
  'inferredMastery',
] as const

/** Statsvelden die mee mogen. `name` staat hier bewust NIET bij. */
const SYNCED_STAT_KEYS = [
  'xp',
  'level',
  'unlockedAchievements',
  'unlockedCosmetics',
  'labSessionsCompleted',
] as const

type Payload = Record<string, unknown>

/** Huidige revisie, nodig om conflicten te detecteren. */
let knownRevision: number | null = null

/** Reset bij accountwissel of uitloggen — anders lekt een revisie over. */
export function resetSyncRevision(): void {
  knownRevision = null
}

/**
 * Filter de leerlingstaat tot wat mag synchroniseren.
 *
 * Exporteerbaar zodat dit testbaar is zonder database: de vraag "wat versturen
 * we precies" moet met een unittest te beantwoorden zijn, niet met een
 * netwerkinspectie.
 */
export function toSyncPayload(learnerState: Record<string, unknown>): Payload {
  const out: Payload = {}
  for (const key of SYNCED_KEYS) {
    if (learnerState[key] !== undefined) out[key] = learnerState[key]
  }

  const stats = learnerState.stats as Record<string, unknown> | undefined
  if (stats) {
    const safeStats: Payload = {}
    for (const key of SYNCED_STAT_KEYS) {
      if (stats[key] !== undefined) safeStats[key] = stats[key]
    }
    out.stats = safeStats
  }
  return out
}

/**
 * Voeg serverdata samen met de lokale staat.
 *
 * De server is leidend voor de gesynchroniseerde velden, maar de lokale NAAM
 * blijft staan — die staat niet op de server, dus zou hem overschrijven met
 * `undefined` de leerling naamloos maken.
 *
 * Bewust GEEN diepe merge per vraag of familie. Dat klinkt slimmer, maar zou
 * betekenen dat een leerling die zijn voortgang wist, die na één sync weer
 * terugkrijgt van het andere apparaat. "Laatste schrijver wint per apparaat,
 * met conflictdetectie" is voorspelbaarder dan een merge die niemand kan
 * navertellen.
 */
export function mergeRemoteIntoLocal(
  local: Record<string, unknown>,
  remote: Payload,
): Record<string, unknown> {
  const localStats = (local.stats as Record<string, unknown>) ?? {}
  const remoteStats = (remote.stats as Record<string, unknown>) ?? {}
  return {
    ...local,
    ...Object.fromEntries(
      SYNCED_KEYS.filter((k) => remote[k] !== undefined).map((k) => [
        k,
        remote[k],
      ]),
    ),
    stats: { ...localStats, ...remoteStats, name: localStats.name },
  }
}

/** Haal de serverversie op. Retourneert null als er nog niets staat. */
export async function fetchSoloProgress(): Promise<Payload | null> {
  const { data, error } = await supabase
    .from('solo_progress')
    .select('payload, revision')
    .maybeSingle()

  if (error) {
    logError('[SoloSync] Ophalen mislukt', error)
    // Revisie wissen, niet laten staan. Bleef hij staan, dan hoort hij bij een
    // sessie waarvan we de stand niet meer kennen — en bij een accountwissel
    // zelfs bij een ánder kind. Een lege revisie geeft hooguit een conflict dat
    // de aanroeper netjes afhandelt; een verkeerde revisie is erger.
    knownRevision = null
    return null
  }
  if (!data) {
    knownRevision = null
    return null
  }
  knownRevision = data.revision as number
  return data.payload as Payload
}

/**
 * Schrijf de voortgang weg.
 *
 * Retourneert false bij een conflict: een ander apparaat was ons voor. De
 * aanroeper moet dan opnieuw ophalen. Stil overschrijven zou betekenen dat een
 * kind dat 's ochtends op de tablet oefende en 's middags op de telefoon, die
 * ochtend kwijt is zonder dat iemand het merkt.
 */
export async function pushSoloProgress(
  learnerState: Record<string, unknown>,
): Promise<boolean> {
  const payload = toSyncPayload(learnerState)
  const { data, error } = await supabase.rpc('save_solo_progress', {
    new_payload: payload,
    expected_revision: knownRevision,
  })

  if (error) {
    logError('[SoloSync] Wegschrijven mislukt', error)
    return false
  }
  if (data === null) {
    debug('[SoloSync] Conflict — ander apparaat was eerder')
    return false
  }
  knownRevision = data as number
  return true
}
